/*
 * Translations for Briefing, Privacy Policy, Terms & Conditions, and Legal Notice.
 * GDPR-compliant. Adapted for natural person / sole trader.
 * Spanish: Spain-localized, formal legal tone.
 *
 * Placeholders: [PLACEHOLDER] — to be filled by the data controller.
 */

const CONTROLLER_NAME = 'Tamara Navarrete Navas';
const CONTACT_EMAIL = 'info@yourbrandvalue.com';
const NIF = '***70720L';
const ADDRESS_ES = 'C/C******, *, P******, Tarragona, España';
const ADDRESS_EN = 'C/C******, *, P******, Tarragona, Spain';

export const pageTranslations = {
  en: {
    // ─── BRIEFING PAGE ───
    briefing: {
      backLink: 'Back to platform overview',
      heading: 'Request an Institutional Briefing',
      subtitle: "Tell us about your organization and what you'd like to evaluate. We'll schedule a tailored walkthrough of the platform and calibration process.",
      nameLabel: 'Full Name *',
      namePlaceholder: 'Jane Smith',
      emailLabel: 'Work Email *',
      emailPlaceholder: 'jane@federation.org',
      orgLabel: 'Organization *',
      orgPlaceholder: 'Federation / Club / Agency',
      roleLabel: 'Your Role',
      rolePlaceholder: 'e.g. Chief Commercial Officer',
      evaluateLabel: 'What would you like to evaluate? *',
      evaluateDefault: 'Select an area...',
      evaluateOptions: [
        'National team / federation brand value',
        'Club brand intelligence',
        'Athlete / coaching staff brand analysis',
        'Sponsorship whitespace & commercial positioning',
        'Tournament scenario & ROI modeling',
        'Competitive benchmarking',
        'Custom engagement',
      ],
      notesLabel: 'Additional Notes',
      notesPlaceholder: 'Timeline, specific assets, questions...',
      consent: 'I accept the',
      consentLink: 'Privacy Policy',
      consentEnd: 'and agree to be contacted regarding my request.',
      submitting: 'Submitting...',
      submit: 'Submit Briefing Request',
      responseNote: "We'll respond within 2 business days. You can also reach us at",
      successTitle: 'Request Received',
      successText: 'Thank you. Our team will review your request and respond within two business days to schedule an institutional briefing.',
      successBtn: 'Return to Platform Overview',
    },

    // ─── PRIVACY POLICY (ENGLISH — GDPR-COMPLIANT) ───
    privacy: {
      backLink: 'Back to platform overview',
      heading: 'Privacy Policy',
      updated: 'Last updated: June 2026',
      sections: [
        {
          title: '1. Data Controller',
          content: [
            `The data controller responsible for the processing of your personal data is ${CONTROLLER_NAME}, with NIF ${NIF} and professional contact address at ${ADDRESS_EN}. "Your Brand Value" (YBV) is a commercial brand operated by ${CONTROLLER_NAME}.`,
            `You may contact us regarding any data-protection matter at: ${CONTACT_EMAIL}, or at the postal address stated above.`,
          ],
        },
        {
          title: '2. Introduction',
          content: [
            'YBV operates the YBV Brand Intelligence Platform. This Privacy Policy explains how we collect, use, store, and protect your personal information when you interact with our platform, including our website, forms, and demo experiences.',
            'By using our platform or submitting information through our forms, you acknowledge that you have read and understood this Privacy Policy.',
          ],
        },
        {
          title: '3. Data We Collect',
          content: [
            'We collect the following personal information when you submit a form on our platform:',
          ],
          list: [
            'Full name',
            'Work email address',
            'Organization name',
            'Professional role or title',
            'Area of interest or evaluation topic',
            'Optional messages or notes you provide',
          ],
          after: 'We may also automatically collect non-personal technical data such as browser type, device information, and page visit timestamps for platform performance purposes.',
        },
        {
          title: '4. Purpose of Data Collection',
          content: ['We use the information we collect for the following purposes:'],
          richList: [
            { bold: 'Lead management:', text: 'To process and respond to your briefing or pilot engagement requests.' },
            { bold: 'Communication:', text: 'To contact you regarding your inquiry, schedule briefings, and provide relevant updates about our platform.' },
            { bold: 'Platform improvement:', text: 'To understand how our services are used and improve the user experience.' },
          ],
        },
        {
          title: '5. Legal Basis for Processing',
          content: ['We process your personal data based on the following legal grounds under Article 6 of the GDPR:'],
          richList: [
            { bold: 'Consent (Art. 6(1)(a)):', text: 'When you submit a form, you explicitly consent to the collection and processing of your data for the stated purposes.' },
            { bold: 'Legitimate interest (Art. 6(1)(f)):', text: 'We may process data to manage and grow our business relationships, provided this does not override your rights and freedoms.' },
          ],
          after: `You may withdraw your consent at any time by contacting us at ${CONTACT_EMAIL}, without affecting the lawfulness of processing based on consent prior to its withdrawal.`,
        },
        {
          title: '6. Data Storage and International Transfers',
          content: [
            'Your personal data is stored using MongoDB database technology. The production server region has not yet been finalized and will be configured to comply with applicable data-protection requirements prior to public launch. Access is restricted to authorized personnel only.',
            `Form submissions are also transmitted via email to our internal team using encrypted SMTP connections (STARTTLS) provided by Namecheap PrivateEmail. We do not have independently verified documentation of the physical server location for this email service.`,
            'Where any service provider processes data outside the European Economic Area, we will ensure appropriate safeguards are in place in accordance with Regulation (EU) 2016/679, including Standard Contractual Clauses (SCCs) approved by the European Commission or applicable adequacy decisions.',
            'We retain your data only for as long as necessary to fulfill the purposes outlined in this policy. Specifically:',
          ],
          list: [
            'Contact, briefing, and pilot request data: 3 years from the last point of contact.',
            'Technical and security logs: 12 months.',
            'Unless a longer retention period is required by applicable law.',
          ],
        },
        {
          title: '7. Third Parties and Data Processors',
          content: ['We may share your data with the following categories of service providers, who act as data processors on our behalf:'],
          richList: [
            { bold: 'Hosting provider:', text: 'Our platform infrastructure is hosted on cloud services that process data on our behalf within the EEA.' },
            { bold: 'Email service provider:', text: 'We use an external SMTP service to deliver notification and confirmation emails.' },
          ],
          after: 'We do not sell, rent, or trade your personal information to any third party for marketing purposes. A current list of sub-processors is available upon request.',
        },
        {
          title: '8. Your Rights',
          content: ['Under the GDPR, you have the following rights regarding your personal data:'],
          richList: [
            { bold: 'Access (Art. 15):', text: 'Request a copy of the personal data we hold about you.' },
            { bold: 'Rectification (Art. 16):', text: 'Request correction of any inaccurate or incomplete data.' },
            { bold: 'Erasure (Art. 17):', text: 'Request deletion of your personal data, subject to any legal retention obligations.' },
            { bold: 'Restriction (Art. 18):', text: 'Request restriction of processing in certain circumstances.' },
            { bold: 'Objection (Art. 21):', text: 'Object to the processing of your data where we rely on legitimate interest.' },
            { bold: 'Portability (Art. 20):', text: 'Request your data in a structured, commonly used, machine-readable format.' },
          ],
          after: `To exercise any of these rights, please contact us at ${CONTACT_EMAIL}. We will respond within one month as required by the GDPR. You also have the right to lodge a complaint with the Spanish Data Protection Agency (Agencia Española de Protección de Datos — AEPD), C/ Jorge Juan 6, 28001 Madrid (www.aepd.es), if you believe the processing of your personal data does not comply with applicable regulations.`,
        },
        {
          title: '9. Automated Decision-Making',
          content: [
            'The Platform uses proprietary analytical models to generate brand scores and indices. These outputs are directional estimates for informational purposes only and do not produce legal effects or automated decisions that significantly affect individuals within the meaning of Article 22 of the GDPR.',
          ],
        },
        {
          title: '10. Cookies and Tracking Technologies',
          content: [
            'Our platform does not use third-party analytics, advertising cookies, or session recording tools. We do not use any tracking technologies that profile visitors or share data with external services.',
            'We may use strictly necessary technical mechanisms (such as browser local storage) for the operation and security of the website. These are limited to preserving your language preference and, for authenticated users, maintaining your session. They do not require consent under applicable data-protection law.',
            'If non-essential analytics or cookies are introduced in the future, we will inform you and request your consent where legally required, via a visible notice on your first visit.',
          ],
        },
        {
          title: '11. Payment Processing',
          content: [
            'When users choose to become a Founding Supporter, payments are processed by Stripe, Inc.',
            'Depending on the transaction, Stripe may process:',
          ],
          list: [
            'Name',
            'Email address',
            'Payment amount',
            'Transaction metadata',
            'Payment method information',
          ],
          after: 'Payment data is processed directly by Stripe according to Stripe\'s Privacy Policy. YBV does not store full payment card information.',
        },
        {
          title: '12. Email Communications',
          content: [
            'We may use MailerLite to manage supporter communications, project updates, newsletters and transactional messages.',
            'Data shared with MailerLite may include:',
          ],
          list: [
            'Name',
            'Email address',
            'Language preference',
            'Supporter status',
          ],
          after: 'MailerLite processes personal data on our behalf under applicable data protection agreements.',
        },
        {
          title: '13. Security',
          content: [
            'We implement appropriate technical and organizational measures to protect your personal data against unauthorized access, alteration, disclosure, or destruction. These include encrypted connections (TLS/SSL), role-based access controls, and secure database configurations.',
          ],
        },
        {
          title: '14. Changes to This Policy',
          content: [
            'We may update this Privacy Policy from time to time. Any changes will be posted on this page with an updated revision date. We encourage you to review this policy periodically.',
          ],
        },
        {
          title: '15. Contact',
          content: [
            'If you have any questions about this Privacy Policy or how we handle your data, please contact us at:',
          ],
          showContact: true,
        },
      ],
      footer: 'YBV Brand Intelligence Platform. All rights reserved.',
    },

    // ─── TERMS & CONDITIONS (ENGLISH — GDPR-COMPLIANT) ───
    terms: {
      backLink: 'Back to platform overview',
      heading: 'Terms & Conditions',
      updated: 'Last updated: June 2026',
      preamble: {
        title: 'Service Provider Identification',
        content: [
          `"Your Brand Value" (YBV) is a commercial brand operated by ${CONTROLLER_NAME}, with NIF ${NIF}, and professional contact address at ${ADDRESS_EN}.`,
          `Contact: ${CONTACT_EMAIL}`,
        ],
      },
      sections: [
        {
          title: '1. Acceptance of Terms',
          content: [
            'By accessing or using the Platform, you agree to be bound by these Terms and Conditions. If you do not agree with any part of these terms, you must not use the Platform.',
            'These terms apply to all visitors, users, and others who access or use the Platform, including its public pages, interactive demos, and authenticated workspaces.',
          ],
        },
        {
          title: '2. Platform Usage',
          content: [
            'The Platform is designed to provide directional brand intelligence, analytics, and scenario planning tools for sports entities including federations, professional clubs, and rights holders.',
            'You agree to:',
          ],
          list: [
            'Use the Platform only for lawful purposes and in accordance with these terms.',
            'Provide accurate and truthful information when submitting forms or creating accounts.',
            'Not attempt to gain unauthorized access to any part of the Platform, its servers, or connected systems.',
            'Not reproduce, distribute, or create derivative works from Platform content without prior written consent.',
          ],
        },
        {
          title: '3. No Guarantee of Financial Results',
          content: [
            'All valuations, projections, indices, and analytics presented on the Platform are directional estimates based on publicly available data signals and proprietary analytical models.',
            'They do not constitute:',
          ],
          list: [
            'Financial advice or certified appraisals',
            'Guarantees of future performance or revenue',
            'Binding commercial valuations for transactional purposes',
          ],
          after: 'Outputs are intended solely for internal strategic planning within authorized recipient organizations. Any business decisions made based on Platform data are taken at the user\'s own risk.',
        },
        {
          title: '4. Intellectual Property',
          content: [
            'All content, features, functionality, design, data models, methodologies, and branding of the Platform are and shall remain the exclusive property of YBV and its licensors.',
            'This includes, but is not limited to:',
          ],
          list: [
            'Brand valuation models and composite index calculations',
            'User interface design, layout, and visual assets',
            'Written copy, reports, and analytical narratives',
            'Software code, algorithms, and database architectures',
          ],
          after: 'You may not copy, modify, distribute, sell, or lease any part of the Platform or its content without express written permission.',
        },
        {
          title: '5. User Accounts and Access',
          content: [
            'Certain areas of the Platform require authentication. You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account.',
            'We reserve the right to suspend or terminate access to any account that violates these terms or engages in unauthorized activity.',
          ],
        },
        {
          title: '6. Confidentiality',
          content: [
            'Information accessed through authenticated tenant workspaces is confidential. You agree not to disclose, share, or distribute any data, reports, or insights obtained through the Platform without authorization from the relevant data owner or YBV.',
          ],
        },
        {
          title: '7. Limitation of Liability',
          content: ['To the fullest extent permitted by applicable law, YBV shall not be liable for any:'],
          list: [
            'Indirect, incidental, special, consequential, or punitive damages',
            'Loss of profits, revenue, data, or business opportunities',
            'Damages arising from your use of or inability to use the Platform',
            'Errors or inaccuracies in Platform content or analytics',
          ],
          after: 'Our total aggregate liability for any claims arising from your use of the Platform shall not exceed the amount you have paid to us in the twelve (12) months preceding the claim. This clause does not exclude or limit liability that cannot be excluded or limited under applicable law.',
        },
        {
          title: '8. Indemnification',
          content: [
            'To the extent permitted by applicable law, you agree to indemnify and hold harmless YBV from any claims, damages, losses, or reasonable expenses (including legal fees) arising from (a) your use of the Platform in breach of these terms, (b) your infringement of any rights of a third party, or (c) any inaccurate information you have provided. This clause does not limit any rights you may have as a consumer under applicable consumer-protection law.',
          ],
        },
        {
          title: '9. Modifications',
          content: [
            'We reserve the right to modify these Terms and Conditions at any time. Changes will be posted on this page with an updated revision date. Your continued use of the Platform after any changes constitutes acceptance of the revised terms.',
          ],
        },
        {
          title: '10. Governing Law and Jurisdiction',
          content: [
            'These Terms and Conditions shall be governed by and construed in accordance with the laws of the Kingdom of Spain.',
            'Any disputes arising from these terms or your use of the Platform shall be submitted to the Courts and Tribunals of Tarragona, Spain, without prejudice to any rights that consumers and users may have under the consumer-protection laws of their country of residence within the European Union.',
          ],
        },
        {
          title: '11. Founding Supporters',
          content: [
            'Users may voluntarily contribute to support the ongoing development of Your Brand Value Sports and World Cup Brand Index™.',
            'These contributions:',
          ],
          list: [
            'are voluntary;',
            'are not charitable donations;',
            'do not constitute investments;',
            'do not represent securities or financial products;',
            'do not grant ownership rights;',
            'do not grant shareholder, partner, investor or membership status;',
            'do not provide financial returns or profit participation;',
            'do not create any obligation for YBV to deliver future products or services.',
          ],
          after: 'Contributions are intended solely to support the continued development and improvement of the platform.',
        },
        {
          title: '12. Refund Policy',
          content: [
            'Founding Supporter contributions are generally non-refundable.',
            'If a payment has been made in error, users may contact:',
          ],
          showContact: true,
          after: 'Refund requests will be reviewed individually and at YBV\'s sole discretion.',
        },
        {
          title: '13. Trademark Disclaimer',
          content: [
            'World Cup Brand Index™ 2026 is an independent brand intelligence and research tool developed by Your Brand Value Sports. It is not affiliated with, sponsored by, endorsed by, or officially connected to FIFA, the FIFA World Cup™, any national football federation, or any official tournament partner.',
            'All brand scores, rankings, valuations and projections are proprietary analytical estimates generated by Your Brand Value Sports and are provided for informational purposes only.',
          ],
        },
        {
          title: '14. Contact',
          content: ['If you have any questions about these Terms and Conditions, please contact us at:'],
          showContact: true,
        },
      ],
      footer: 'YBV Brand Intelligence Platform. All rights reserved.',
    },

    // ─── LEGAL NOTICE (ENGLISH) ───
    legal: {
      backLink: 'Back to platform overview',
      heading: 'Legal Notice',
      updated: 'Last updated: June 2026',
      sections: [
        {
          title: 'Service Provider',
          content: [
            `In compliance with Spain\'s Law 34/2002 on Information Society Services and Electronic Commerce (LSSI-CE), the following identification details are provided. "Your Brand Value" (YBV) is a commercial brand operated by the individual identified below.`,
          ],
          richList: [
            { bold: 'Service provider:', text: CONTROLLER_NAME },
            { bold: 'NIF:', text: NIF },
            { bold: 'Professional contact address:', text: ADDRESS_EN },
            { bold: 'Contact email:', text: CONTACT_EMAIL },
          ],
        },
        {
          title: 'Purpose',
          content: [
            'This website provides information about the YBV Brand Intelligence Platform and its services in the field of brand intelligence and commercial consulting for the sports industry.',
          ],
        },
        {
          title: 'Intellectual Property',
          content: [
            'All content on this website — including but not limited to text, graphics, images, design, data models, software, and brand identity — is the property of YBV or its licensors and is protected by applicable intellectual property laws.',
            'Reproduction, distribution, or modification of any content without express written authorization is prohibited.',
          ],
        },
        {
          title: 'Limitation of Liability',
          content: [
            'YBV endeavors to keep the information on this website accurate and up to date, but does not guarantee the absence of errors or omissions. All brand valuations and analytics are directional estimates and do not constitute financial advice.',
            'YBV shall not be liable for damages arising from the use of this website or reliance on the information it contains, except where such liability cannot be excluded by law.',
          ],
        },
        {
          title: 'Links to Other Websites',
          content: [
            'This website may contain links to third-party websites. YBV does not control and is not responsible for the content, privacy policies, or practices of any third-party sites.',
          ],
        },
        {
          title: 'Applicable Law',
          content: [
            'This Legal Notice is governed by the laws of the Kingdom of Spain. For matters related to personal data protection, please refer to our Privacy Policy. For the terms governing use of the Platform, please refer to our Terms & Conditions.',
          ],
        },
        {
          title: 'Trademark Disclaimer',
          content: [
            'World Cup Brand Index™ 2026 is an independent brand intelligence and research tool developed by Your Brand Value Sports. It is not affiliated with, sponsored by, endorsed by, or officially connected to FIFA, the FIFA World Cup™, any national football federation, or any official tournament partner.',
            'All brand scores, rankings, valuations and projections are proprietary analytical estimates generated by Your Brand Value Sports and are provided for informational purposes only.',
          ],
        },
      ],
      footer: 'YBV Brand Intelligence Platform. All rights reserved.',
    },
  },

  es: {
    // ─── BRIEFING PAGE (SPANISH) ───
    briefing: {
      backLink: 'Volver a la vista general',
      heading: 'Solicitar un Briefing Institucional',
      subtitle: 'Cuéntanos sobre tu organización y qué te gustaría evaluar. Programaremos una presentación personalizada de la plataforma y del proceso de calibración.',
      nameLabel: 'Nombre Completo *',
      namePlaceholder: 'María García',
      emailLabel: 'Email Profesional *',
      emailPlaceholder: 'maria@federacion.es',
      orgLabel: 'Organización *',
      orgPlaceholder: 'Federación / Club / Agencia',
      roleLabel: 'Tu Cargo',
      rolePlaceholder: 'p. ej. Director/a Comercial',
      evaluateLabel: '¿Qué te gustaría evaluar? *',
      evaluateDefault: 'Selecciona un área...',
      evaluateOptions: [
        'Valor de marca de selección / federación',
        'Inteligencia de marca de club',
        'Análisis de marca de deportistas / cuerpo técnico',
        'Espacio de patrocinio y posicionamiento comercial',
        'Modelado de escenarios de torneo y ROI',
        'Benchmarking competitivo',
        'Proyecto personalizado',
      ],
      notesLabel: 'Notas Adicionales',
      notesPlaceholder: 'Plazos, activos específicos, preguntas...',
      consent: 'Acepto la',
      consentLink: 'Política de Privacidad',
      consentEnd: 'y consiento ser contactado/a en relación con mi solicitud.',
      submitting: 'Enviando...',
      submit: 'Enviar Solicitud de Briefing',
      responseNote: 'Responderemos en un plazo de 2 días laborables. También puedes contactarnos en',
      successTitle: 'Solicitud Recibida',
      successText: 'Gracias. Nuestro equipo revisará tu solicitud y responderá en un plazo de dos días laborables para programar un briefing institucional.',
      successBtn: 'Volver a la Vista General',
    },

    // ─── PRIVACY POLICY (SPANISH — GDPR / RGPD-COMPLIANT) ───
    privacy: {
      backLink: 'Volver a la vista general',
      heading: 'Política de Privacidad',
      updated: 'Última actualización: junio 2026',
      sections: [
        {
          title: '1. Responsable del Tratamiento',
          content: [
            `El responsable del tratamiento de tus datos personales es ${CONTROLLER_NAME}, con NIF ${NIF} y dirección de contacto profesional en ${ADDRESS_ES}. "Your Brand Value" (YBV) es una marca comercial operada por ${CONTROLLER_NAME}.`,
            `Puedes contactar con nosotros en relación con cualquier cuestión de protección de datos en: ${CONTACT_EMAIL}, o en la dirección postal indicada anteriormente.`,
          ],
        },
        {
          title: '2. Introducción',
          content: [
            'YBV opera la Plataforma de Inteligencia de Marca YBV. Esta Política de Privacidad explica cómo recogemos, utilizamos, almacenamos y protegemos tu información personal cuando interactúas con nuestra plataforma, incluyendo nuestro sitio web, formularios y experiencias de demostración.',
            'Al utilizar nuestra plataforma o enviar información a través de nuestros formularios, reconoces que has leído y comprendido esta Política de Privacidad.',
          ],
        },
        {
          title: '3. Datos que Recogemos',
          content: ['Recogemos la siguiente información personal cuando envías un formulario en nuestra plataforma:'],
          list: [
            'Nombre completo',
            'Dirección de correo electrónico profesional',
            'Nombre de la organización',
            'Cargo o función profesional',
            'Área de interés o tema de evaluación',
            'Mensajes o notas opcionales que proporciones',
          ],
          after: 'También podemos recoger automáticamente datos técnicos no personales como tipo de navegador, información del dispositivo y marcas temporales de visitas para fines de rendimiento de la plataforma.',
        },
        {
          title: '4. Finalidad de la Recogida de Datos',
          content: ['Utilizamos la información que recogemos con las siguientes finalidades:'],
          richList: [
            { bold: 'Gestión de leads:', text: 'Para procesar y responder a tus solicitudes de briefing o de programa piloto.' },
            { bold: 'Comunicación:', text: 'Para contactarte en relación con tu consulta, programar briefings y proporcionarte actualizaciones relevantes sobre nuestra plataforma.' },
            { bold: 'Mejora de la plataforma:', text: 'Para entender cómo se utilizan nuestros servicios y mejorar la experiencia del usuario.' },
          ],
        },
        {
          title: '5. Base Legal del Tratamiento',
          content: ['Tratamos tus datos personales sobre las siguientes bases legales conforme al artículo 6 del RGPD:'],
          richList: [
            { bold: 'Consentimiento (art. 6.1.a):', text: 'Al enviar un formulario, consientes explícitamente la recogida y el tratamiento de tus datos para los fines indicados.' },
            { bold: 'Interés legítimo (art. 6.1.f):', text: 'Podemos tratar datos para gestionar y desarrollar nuestras relaciones comerciales, siempre que esto no prevalezca sobre tus derechos y libertades.' },
          ],
          after: `Puedes retirar tu consentimiento en cualquier momento contactándonos en ${CONTACT_EMAIL}, sin que ello afecte a la licitud del tratamiento basado en el consentimiento previo a su retirada.`,
        },
        {
          title: '6. Almacenamiento de Datos y Transferencias Internacionales',
          content: [
            'Tus datos personales se almacenan utilizando tecnología de base de datos MongoDB. La región del servidor de producción aún no ha sido definida y se configurará conforme a los requisitos de protección de datos aplicables antes del lanzamiento público. El acceso está restringido exclusivamente a personal autorizado.',
            'Los envíos de formularios también se transmiten por correo electrónico a nuestro equipo interno mediante conexiones SMTP cifradas (STARTTLS) proporcionadas por Namecheap PrivateEmail. No disponemos de documentación verificada de forma independiente sobre la ubicación física de los servidores de dicho servicio de correo electrónico.',
            'En caso de que algún proveedor de servicios trate datos fuera del Espacio Económico Europeo, nos aseguraremos de que existan las garantías adecuadas conforme al Reglamento (UE) 2016/679, incluyendo Cláusulas Contractuales Tipo (CCT) aprobadas por la Comisión Europea o decisiones de adecuación vigentes.',
            'Conservamos tus datos únicamente durante el tiempo necesario para cumplir las finalidades descritas en esta política. En concreto:',
          ],
          list: [
            'Datos de contacto, briefing y solicitudes piloto: 3 años desde el último contacto.',
            'Registros técnicos y de seguridad: 12 meses.',
            'Salvo que la ley exija un período de conservación más largo.',
          ],
        },
        {
          title: '7. Terceros y Encargados del Tratamiento',
          content: ['Podemos compartir tus datos con las siguientes categorías de proveedores de servicios, que actúan como encargados del tratamiento por nuestra cuenta:'],
          richList: [
            { bold: 'Proveedor de alojamiento:', text: 'La infraestructura de nuestra plataforma está alojada en servicios en la nube que procesan datos en nuestro nombre dentro del EEE.' },
            { bold: 'Proveedor de correo electrónico:', text: 'Utilizamos un servicio SMTP externo para enviar correos de notificación y confirmación.' },
          ],
          after: 'No vendemos, alquilamos ni cedemos tu información personal a terceros con fines comerciales. La lista actualizada de encargados del tratamiento está disponible previa solicitud.',
        },
        {
          title: '8. Tus Derechos',
          content: ['Conforme al RGPD, tienes los siguientes derechos respecto a tus datos personales:'],
          richList: [
            { bold: 'Acceso (art. 15):', text: 'Solicitar una copia de los datos personales que conservamos sobre ti.' },
            { bold: 'Rectificación (art. 16):', text: 'Solicitar la corrección de datos inexactos o incompletos.' },
            { bold: 'Supresión (art. 17):', text: 'Solicitar la eliminación de tus datos personales, sujeta a obligaciones legales de conservación.' },
            { bold: 'Limitación (art. 18):', text: 'Solicitar la limitación del tratamiento en determinadas circunstancias.' },
            { bold: 'Oposición (art. 21):', text: 'Oponerte al tratamiento de tus datos cuando nos basamos en el interés legítimo.' },
            { bold: 'Portabilidad (art. 20):', text: 'Solicitar tus datos en un formato estructurado, de uso común y lectura mecánica.' },
          ],
          after: `Para ejercer cualquiera de estos derechos, contacta con nosotros en ${CONTACT_EMAIL}. Responderemos en el plazo de un mes conforme a lo establecido en el RGPD. Asimismo, tienes derecho a presentar una reclamación ante la Agencia Española de Protección de Datos (AEPD), C/ Jorge Juan 6, 28001 Madrid (www.aepd.es), si consideras que el tratamiento de tus datos personales no se ajusta a la normativa vigente.`,
        },
        {
          title: '9. Decisiones Automatizadas',
          content: [
            'La Plataforma utiliza modelos analíticos propietarios para generar puntuaciones e índices de marca. Estos resultados son estimaciones direccionales de carácter exclusivamente informativo y no producen efectos jurídicos ni decisiones automatizadas que afecten significativamente a las personas físicas en el sentido del artículo 22 del RGPD.',
          ],
        },
        {
          title: '10. Cookies y Tecnologías de Seguimiento',
          content: [
            'Nuestra plataforma no utiliza herramientas de analítica de terceros, cookies publicitarias ni grabación de sesiones. No empleamos tecnologías de seguimiento que perfilen a los visitantes ni compartan datos con servicios externos.',
            'Podemos utilizar mecanismos técnicos estrictamente necesarios (como el almacenamiento local del navegador) para el funcionamiento y la seguridad del sitio web. Estos se limitan a conservar tu preferencia de idioma y, para usuarios autenticados, a mantener la sesión. No requieren consentimiento conforme a la normativa de protección de datos aplicable.',
            'En caso de que se introduzcan analíticas o cookies no esenciales en el futuro, te informaremos y solicitaremos tu consentimiento cuando sea legalmente exigible, mediante un aviso visible en tu primera visita.',
          ],
        },
        {
          title: '11. Procesamiento de Pagos',
          content: [
            'Cuando los usuarios deciden convertirse en Founding Supporters, los pagos son procesados por Stripe, Inc.',
            'Dependiendo de la transacción, Stripe puede procesar:',
          ],
          list: [
            'Nombre',
            'Dirección de correo electrónico',
            'Importe de la aportación',
            'Metadatos de la transacción',
            'Información del método de pago',
          ],
          after: 'Los datos de pago son tratados directamente por Stripe de acuerdo con su Política de Privacidad. YBV no almacena información completa de tarjetas bancarias.',
        },
        {
          title: '12. Comunicaciones por Correo Electrónico',
          content: [
            'Podemos utilizar MailerLite para gestionar comunicaciones con supporters, actualizaciones del proyecto, newsletters y mensajes transaccionales.',
            'Los datos compartidos con MailerLite pueden incluir:',
          ],
          list: [
            'Nombre',
            'Dirección de correo electrónico',
            'Preferencia de idioma',
            'Estado de supporter',
          ],
          after: 'MailerLite trata los datos personales por cuenta de YBV bajo los acuerdos de protección de datos aplicables.',
        },
        {
          title: '13. Seguridad',
          content: [
            'Implementamos medidas técnicas y organizativas adecuadas para proteger tus datos personales frente a accesos no autorizados, alteración, divulgación o destrucción. Estas incluyen conexiones cifradas (TLS/SSL), controles de acceso basados en roles y configuraciones seguras de bases de datos.',
          ],
        },
        {
          title: '14. Cambios en esta Política',
          content: [
            'Podemos actualizar esta Política de Privacidad periódicamente. Los cambios se publicarán en esta página con una fecha de revisión actualizada. Te recomendamos revisar esta política de forma periódica.',
          ],
        },
        {
          title: '15. Contacto',
          content: ['Si tienes alguna pregunta sobre esta Política de Privacidad o sobre cómo gestionamos tus datos, contacta con nosotros en:'],
          showContact: true,
        },
      ],
      footer: 'YBV Brand Intelligence Platform. Todos los derechos reservados.',
    },

    // ─── TERMS (SPANISH — GDPR / RGPD-COMPLIANT) ───
    terms: {
      backLink: 'Volver a la vista general',
      heading: 'Términos y Condiciones',
      updated: 'Última actualización: junio 2026',
      preamble: {
        title: 'Identificación del Prestador del Servicio',
        content: [
          `"Your Brand Value" (YBV) es una marca comercial operada por ${CONTROLLER_NAME}, con NIF ${NIF} y dirección de contacto profesional en ${ADDRESS_ES}.`,
          `Contacto: ${CONTACT_EMAIL}`,
        ],
      },
      sections: [
        {
          title: '1. Aceptación de los Términos',
          content: [
            'Al acceder o utilizar la Plataforma, aceptas quedar vinculado por estos Términos y Condiciones. Si no estás de acuerdo con alguna parte de estos términos, no debes utilizar la Plataforma.',
            'Estos términos se aplican a todos los visitantes, usuarios y demás personas que accedan o utilicen la Plataforma, incluyendo sus páginas públicas, demos interactivas y espacios de trabajo autenticados.',
          ],
        },
        {
          title: '2. Uso de la Plataforma',
          content: [
            'La Plataforma está diseñada para proporcionar herramientas direccionales de inteligencia de marca, analítica y planificación de escenarios para entidades deportivas, incluyendo federaciones, clubes profesionales y titulares de derechos.',
            'Te comprometes a:',
          ],
          list: [
            'Utilizar la Plataforma únicamente con fines lícitos y de acuerdo con estos términos.',
            'Proporcionar información veraz y exacta al enviar formularios o crear cuentas.',
            'No intentar obtener acceso no autorizado a ninguna parte de la Plataforma, sus servidores o sistemas conectados.',
            'No reproducir, distribuir ni crear obras derivadas del contenido de la Plataforma sin consentimiento previo por escrito.',
          ],
        },
        {
          title: '3. Sin Garantía de Resultados Financieros',
          content: [
            'Todas las valoraciones, proyecciones, índices y analíticas presentadas en la Plataforma son estimaciones direccionales basadas en señales de datos públicos y modelos analíticos propietarios.',
            'No constituyen:',
          ],
          list: [
            'Asesoramiento financiero ni tasaciones certificadas',
            'Garantías de rendimiento futuro o ingresos',
            'Valoraciones comerciales vinculantes para fines transaccionales',
          ],
          after: 'Los resultados están destinados exclusivamente a la planificación estratégica interna dentro de las organizaciones receptoras autorizadas. Cualquier decisión empresarial tomada en base a datos de la Plataforma se realiza bajo la responsabilidad del usuario.',
        },
        {
          title: '4. Propiedad Intelectual',
          content: [
            'Todo el contenido, funcionalidades, diseño, modelos de datos, metodologías y marca de la Plataforma son y seguirán siendo propiedad exclusiva de YBV y sus licenciantes.',
            'Esto incluye, sin limitación:',
          ],
          list: [
            'Modelos de valoración de marca y cálculos de índices compuestos',
            'Diseño de interfaz de usuario, maquetación y activos visuales',
            'Textos, informes y narrativas analíticas',
            'Código de software, algoritmos y arquitecturas de bases de datos',
          ],
          after: 'No está permitido copiar, modificar, distribuir, vender ni licenciar ninguna parte de la Plataforma o su contenido sin autorización expresa por escrito.',
        },
        {
          title: '5. Cuentas de Usuario y Acceso',
          content: [
            'Determinadas áreas de la Plataforma requieren autenticación. Eres responsable de mantener la confidencialidad de tus credenciales de acceso y de todas las actividades que se realicen bajo tu cuenta.',
            'Nos reservamos el derecho de suspender o cancelar el acceso a cualquier cuenta que incumpla estos términos o realice actividades no autorizadas.',
          ],
        },
        {
          title: '6. Confidencialidad',
          content: [
            'La información accedida a través de los espacios de trabajo autenticados de clientes es confidencial. Te comprometes a no divulgar, compartir ni distribuir datos, informes ni información obtenida a través de la Plataforma sin la autorización del titular de los datos o de YBV.',
          ],
        },
        {
          title: '7. Limitación de Responsabilidad',
          content: ['En la máxima medida permitida por la legislación aplicable, YBV no será responsable de:'],
          list: [
            'Daños indirectos, incidentales, especiales, consecuenciales o punitivos',
            'Pérdida de beneficios, ingresos, datos u oportunidades de negocio',
            'Daños derivados de tu uso o imposibilidad de uso de la Plataforma',
            'Errores o inexactitudes en el contenido o analíticas de la Plataforma',
          ],
          after: 'La responsabilidad total acumulada de YBV por cualquier reclamación derivada del uso de la Plataforma no excederá el importe que nos hayas abonado en los doce (12) meses anteriores a la reclamación. Esta cláusula no excluye ni limita la responsabilidad que no pueda excluirse o limitarse conforme a la legislación aplicable.',
        },
        {
          title: '8. Indemnización',
          content: [
            'En la medida permitida por la legislación aplicable, te comprometes a indemnizar y mantener indemne a YBV frente a reclamaciones, daños, pérdidas o gastos razonables (incluidos honorarios legales) derivados de (a) el uso que hagas de la Plataforma incumpliendo estos términos, (b) la vulneración de derechos de terceros, o (c) cualquier información inexacta que hayas proporcionado. Esta cláusula no limita los derechos que te correspondan como consumidor conforme a la normativa de protección de consumidores aplicable.',
          ],
        },
        {
          title: '9. Modificaciones',
          content: [
            'Nos reservamos el derecho de modificar estos Términos y Condiciones en cualquier momento. Los cambios se publicarán en esta página con una fecha de revisión actualizada. El uso continuado de la Plataforma tras cualquier cambio constituye la aceptación de los términos revisados.',
          ],
        },
        {
          title: '10. Legislación Aplicable y Jurisdicción',
          content: [
            'Estos Términos y Condiciones se regirán e interpretarán de conformidad con la legislación del Reino de España.',
            'Para la resolución de cualquier controversia derivada de estos términos o del uso de la Plataforma, las partes se someten a los Juzgados y Tribunales de Tarragona, sin perjuicio de los derechos que pudieran corresponder a los consumidores y usuarios conforme a la normativa de protección de consumidores de su país de residencia dentro de la Unión Europea.',
          ],
        },
        {
          title: '11. Founding Supporters',
          content: [
            'Los usuarios pueden realizar aportaciones voluntarias para apoyar el desarrollo continuo de Your Brand Value Sports y World Cup Brand Index™.',
            'Estas aportaciones:',
          ],
          list: [
            'son voluntarias;',
            'no constituyen donaciones benéficas;',
            'no constituyen inversiones;',
            'no representan valores ni productos financieros;',
            'no otorgan derechos de propiedad;',
            'no generan la condición de socio, accionista, inversor o miembro;',
            'no generan retornos económicos ni participación en beneficios;',
            'no crean obligación alguna para YBV de entregar futuros productos o servicios.',
          ],
          after: 'Las aportaciones tienen como único objetivo apoyar el desarrollo y mejora continua de la plataforma.',
        },
        {
          title: '12. Política de Reembolsos',
          content: [
            'Las aportaciones realizadas como Founding Supporter son generalmente no reembolsables.',
            'Si un pago se ha realizado por error, los usuarios pueden contactar con:',
          ],
          showContact: true,
          after: 'Las solicitudes de reembolso serán revisadas individualmente y quedarán sujetas al criterio de YBV.',
        },
        {
          title: '13. Aviso sobre Marcas Registradas',
          content: [
            'World Cup Brand Index™ 2026 es una herramienta independiente de inteligencia e investigación de marca desarrollada por Your Brand Value Sports. No está afiliada, patrocinada, avalada ni vinculada oficialmente con FIFA, la Copa Mundial de la FIFA™, ninguna federación nacional de fútbol ni ningún patrocinador oficial del torneo.',
            'Todos los Brand Scores, rankings, valoraciones y proyecciones son estimaciones analíticas propias de Your Brand Value Sports y se ofrecen únicamente con fines informativos.',
          ],
        },
        {
          title: '14. Contacto',
          content: ['Si tienes alguna pregunta sobre estos Términos y Condiciones, contacta con nosotros en:'],
          showContact: true,
        },
      ],
      footer: 'YBV Brand Intelligence Platform. Todos los derechos reservados.',
    },

    // ─── LEGAL NOTICE / AVISO LEGAL (SPANISH — LSSI) ───
    legal: {
      backLink: 'Volver a la vista general',
      heading: 'Aviso Legal',
      updated: 'Última actualización: junio 2026',
      sections: [
        {
          title: 'Identificación del Titular',
          content: [
            'En cumplimiento de la Ley 34/2002, de 11 de julio, de Servicios de la Sociedad de la Información y de Comercio Electrónico (LSSI-CE), se facilitan los siguientes datos de identificación. "Your Brand Value" (YBV) es una marca comercial operada por la persona física que se identifica a continuación.',
          ],
          richList: [
            { bold: 'Prestadora del servicio:', text: CONTROLLER_NAME },
            { bold: 'NIF:', text: NIF },
            { bold: 'Dirección de contacto profesional:', text: ADDRESS_ES },
            { bold: 'Email de contacto:', text: CONTACT_EMAIL },
          ],
        },
        {
          title: 'Objeto',
          content: [
            'Este sitio web tiene como finalidad proporcionar información sobre la Plataforma de Inteligencia de Marca YBV y sus servicios en el ámbito de la inteligencia de marca y consultoría comercial para el sector deportivo.',
          ],
        },
        {
          title: 'Propiedad Intelectual',
          content: [
            'Todos los contenidos de este sitio web — incluyendo, sin limitación, textos, gráficos, imágenes, diseño, modelos de datos, software e identidad de marca — son propiedad de YBV o de sus licenciantes y están protegidos por la legislación vigente en materia de propiedad intelectual e industrial.',
            'Queda prohibida la reproducción, distribución o modificación de cualquier contenido sin autorización expresa por escrito.',
          ],
        },
        {
          title: 'Limitación de Responsabilidad',
          content: [
            'YBV procura mantener la información de este sitio web actualizada y correcta, pero no garantiza la ausencia de errores u omisiones. Todas las valoraciones de marca y analíticas son estimaciones direccionales y no constituyen asesoramiento financiero.',
            'YBV no será responsable de los daños que puedan derivarse del uso de este sitio web o de la confianza en la información contenida en el mismo, salvo en aquellos supuestos en los que la ley no permita la exclusión de dicha responsabilidad.',
          ],
        },
        {
          title: 'Enlaces a Otros Sitios Web',
          content: [
            'Este sitio web puede contener enlaces a sitios web de terceros. YBV no controla ni es responsable del contenido, las políticas de privacidad ni las prácticas de sitios de terceros.',
          ],
        },
        {
          title: 'Legislación Aplicable',
          content: [
            'El presente Aviso Legal se rige por la legislación del Reino de España. Para cuestiones relacionadas con la protección de datos personales, consulta nuestra Política de Privacidad. Para las condiciones que regulan el uso de la Plataforma, consulta nuestros Términos y Condiciones.',
          ],
        },
        {
          title: 'Aviso sobre Marcas Registradas',
          content: [
            'World Cup Brand Index™ 2026 es una herramienta independiente de inteligencia e investigación de marca desarrollada por Your Brand Value Sports. No está afiliada, patrocinada, avalada ni vinculada oficialmente con FIFA, la Copa Mundial de la FIFA™, ninguna federación nacional de fútbol ni ningún patrocinador oficial del torneo.',
            'Todos los Brand Scores, rankings, valoraciones y proyecciones son estimaciones analíticas propias de Your Brand Value Sports y se ofrecen únicamente con fines informativos.',
          ],
        },
      ],
      footer: 'YBV Brand Intelligence Platform. Todos los derechos reservados.',
    },
  },
};
